import React from "react";
import { FaPlus, FaTimes, FaPlay, FaUndo } from "react-icons/fa";
import ConditionRow from "./ConditionRow";
import { DATE_BUCKETS, METRIC_FUNCTIONS, newFilter, sortChoices } from "./dataToolsUtils";

const inputClass =
    "w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 bg-white";

const Section = ({ step, title, hint, children }) => (
    <div className="p-5 border-b border-gray-100 space-y-3">
        <div>
            <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center">{step}</span>
                {title}
            </h3>
            {hint && <p className="text-xs text-gray-500 mt-1 ml-8">{hint}</p>}
        </div>
        {children}
    </div>
);

const QueryBuilder = ({ dataset, operators, query, onChange, onRun, onReset, running, workshopId }) => {
    const set = (patch) => onChange({ ...query, ...patch });
    const setSummary = (patch) => onChange({ ...query, summary: { ...query.summary, ...patch }, summarySort: null });

    const updateFilter = (i, next) => set({ filters: query.filters.map((f, idx) => (idx === i ? next : f)) });
    const removeFilter = (i) => set({ filters: query.filters.filter((_, idx) => idx !== i) });
    // Start new conditions on the dataset's main date (e.g. Due Date) — the most common thing people filter by.
    const addFilter = () => {
        const field = dataset.fields.find((f) => f.key === dataset.defaultSortField) || dataset.fields[0];
        set({ filters: [...query.filters, newFilter(field, operators)] });
    };

    const toggleColumn = (key) =>
        set({
            columns: query.columns.includes(key)
                ? query.columns.filter((c) => c !== key)
                : dataset.fields.filter((f) => f.key === key || query.columns.includes(f.key)).map((f) => f.key),
        });

    const sortField = dataset.fields.find((f) => f.key === query.sort?.field);
    const groupField = dataset.fields.find((f) => f.key === query.summary.groupBy);
    const numericOrDateFields = (fn) => dataset.fields.filter((f) => (fn.types || []).includes(f.type));

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100">
            {/* 1. List or summary */}
            <Section step="1" title="What would you like to see?">
                <div className="grid grid-cols-2 gap-2">
                    {[
                        { value: "LIST", title: "A list", sub: "Every matching record" },
                        { value: "SUMMARY", title: "A summary", sub: "Totals, counts & charts" },
                    ].map((m) => (
                        <button
                            key={m.value}
                            onClick={() => set({ mode: m.value })}
                            className={`text-left p-3 rounded-lg border-2 transition ${
                                query.mode === m.value ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-gray-300"
                            }`}
                        >
                            <div className="font-semibold text-gray-800 text-sm">{m.title}</div>
                            <div className="text-xs text-gray-500">{m.sub}</div>
                        </button>
                    ))}
                </div>
            </Section>

            {/* 2. Conditions */}
            <Section
                step="2"
                title="Only include rows where…"
                hint={query.filters.length === 0 ? "No conditions yet — everything will be included." : null}
            >
                {query.filters.length > 1 && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                        <span>Match</span>
                        <select
                            className="border border-gray-300 rounded-lg px-2 py-1 text-sm bg-white"
                            value={query.matchType}
                            onChange={(e) => set({ matchType: e.target.value })}
                        >
                            <option value="ALL">all of these conditions</option>
                            <option value="ANY">any one of these conditions</option>
                        </select>
                    </div>
                )}
                {query.filters.map((f, i) => (
                    <ConditionRow
                        key={i}
                        dataset={dataset}
                        filter={f}
                        operators={operators}
                        workshopId={workshopId}
                        onChange={(next) => updateFilter(i, next)}
                        onRemove={() => removeFilter(i)}
                    />
                ))}
                <button onClick={addFilter} className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-900">
                    <FaPlus className="mr-2" /> Add a condition
                </button>
            </Section>

            {/* 3. List options or summary options */}
            {query.mode === "LIST" ? (
                <Section step="3" title="Columns & order">
                    <div className="flex justify-between text-xs">
                        <span className="text-gray-500">{query.columns.length} of {dataset.fields.length} columns</span>
                        <span className="space-x-3">
                            <button className="text-teal-700 hover:underline" onClick={() => set({ columns: dataset.fields.map((f) => f.key) })}>All</button>
                            <button className="text-teal-700 hover:underline"
                                    onClick={() => set({ columns: dataset.fields.filter((f) => f.defaultVisible).map((f) => f.key) })}>Default</button>
                        </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 max-h-56 overflow-y-auto pr-1">
                        {dataset.fields.map((f) => (
                            <label key={f.key} className="flex items-center gap-2 text-sm text-gray-700 px-2 py-1 rounded hover:bg-gray-50 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="rounded text-teal-600 focus:ring-teal-500"
                                    checked={query.columns.includes(f.key)}
                                    onChange={() => toggleColumn(f.key)}
                                />
                                <span className="truncate">{f.label}</span>
                            </label>
                        ))}
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label className="text-xs text-gray-500">Sort by</label>
                            <select
                                className={inputClass}
                                value={query.sort?.field}
                                onChange={(e) => {
                                    const f = dataset.fields.find((x) => x.key === e.target.value);
                                    set({ sort: { field: f.key, direction: sortChoices(f.type)[0].value } });
                                }}
                            >
                                {dataset.fields.map((f) => (
                                    <option key={f.key} value={f.key}>{f.label}</option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-xs text-gray-500">Order</label>
                            <select
                                className={inputClass}
                                value={query.sort?.direction}
                                onChange={(e) => set({ sort: { ...query.sort, direction: e.target.value } })}
                            >
                                {sortChoices(sortField?.type).map((c) => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <div>
                        <label className="text-xs text-gray-500">Show up to</label>
                        <select className={inputClass} value={query.limit} onChange={(e) => set({ limit: Number(e.target.value) })}>
                            {[25, 50, 100, 250, 500, 1000].map((n) => (
                                <option key={n} value={n}>{n} rows</option>
                            ))}
                        </select>
                    </div>
                </Section>
            ) : (
                <Section step="3" title="How should it be summarised?">
                    <div>
                        <label className="text-xs text-gray-500">Group the results by</label>
                        <div className="flex gap-2">
                            <select className={inputClass} value={query.summary.groupBy} onChange={(e) => setSummary({ groupBy: e.target.value })}>
                                <option value="">Nothing — just one overall total</option>
                                {dataset.fields.filter((f) => f.groupable).map((f) => (
                                    <option key={f.key} value={f.key}>{f.label}</option>
                                ))}
                            </select>
                            {groupField?.type === "DATE" && (
                                <select
                                    className={`${inputClass} w-36`}
                                    value={query.summary.dateBucket}
                                    onChange={(e) => setSummary({ dateBucket: e.target.value })}
                                >
                                    {DATE_BUCKETS.map((b) => (
                                        <option key={b.value} value={b.value}>{b.label}</option>
                                    ))}
                                </select>
                            )}
                        </div>
                    </div>
                    <div className="space-y-2">
                        <label className="text-xs text-gray-500">And calculate</label>
                        {query.summary.metrics.map((m, i) => {
                            const fn = METRIC_FUNCTIONS.find((x) => x.value === m.function);
                            const updateMetric = (patch) =>
                                setSummary({ metrics: query.summary.metrics.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) });
                            return (
                                <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-3 flex items-start gap-2">
                                    <div className="flex-1 space-y-2">
                                        <select
                                            className={inputClass}
                                            value={m.function}
                                            onChange={(e) => {
                                                const nextFn = METRIC_FUNCTIONS.find((x) => x.value === e.target.value);
                                                const keep = nextFn.needsField && dataset.fields.some((f) => f.key === m.field && nextFn.types.includes(f.type));
                                                updateMetric({
                                                    function: nextFn.value,
                                                    field: nextFn.needsField ? (keep ? m.field : numericOrDateFields(nextFn)[0]?.key) : null,
                                                });
                                            }}
                                        >
                                            {METRIC_FUNCTIONS.map((x) => (
                                                <option key={x.value} value={x.value}>{x.label}</option>
                                            ))}
                                        </select>
                                        {fn?.needsField && (
                                            <select className={inputClass} value={m.field || ""} onChange={(e) => updateMetric({ field: e.target.value })}>
                                                {numericOrDateFields(fn).map((f) => (
                                                    <option key={f.key} value={f.key}>{f.label}</option>
                                                ))}
                                            </select>
                                        )}
                                    </div>
                                    {query.summary.metrics.length > 1 && (
                                        <button
                                            onClick={() => setSummary({ metrics: query.summary.metrics.filter((_, idx) => idx !== i) })}
                                            className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                            title="Remove this calculation"
                                        >
                                            <FaTimes />
                                        </button>
                                    )}
                                </div>
                            );
                        })}
                        {query.summary.metrics.length < 6 && (
                            <button
                                onClick={() => {
                                    const money = dataset.fields.find((f) => f.type === "MONEY");
                                    setSummary({ metrics: [...query.summary.metrics, money ? { function: "SUM", field: money.key } : { function: "COUNT", field: null }] });
                                }}
                                className="inline-flex items-center text-sm font-medium text-teal-700 hover:text-teal-900"
                            >
                                <FaPlus className="mr-2" /> Add a calculation
                            </button>
                        )}
                    </div>
                </Section>
            )}

            <div className="p-5 flex gap-2">
                <button
                    onClick={onRun}
                    disabled={running}
                    className="flex-1 inline-flex items-center justify-center px-5 py-2.5 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition shadow-sm disabled:opacity-60"
                >
                    <FaPlay className="mr-2" /> {running ? "Getting results…" : "Show results"}
                </button>
                <button
                    onClick={onReset}
                    className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 transition"
                    title="Start over"
                >
                    <FaUndo />
                </button>
            </div>
        </div>
    );
};

export default QueryBuilder;
